import { Alert, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import type { StudioRuntimeSettings } from './studioApi';
import { updateRuntimeGroup } from './studioRuntimeSettings';
type Session = NonNullable<StudioRuntimeSettings['session']>;
const integer = (value: number | undefined, min: number, max: number) =>
  value === undefined || (Number.isInteger(value) && value >= min && value <= max);
export const validConversationTimezone = (value: string) => {
  if (!value || value.length > 100 || !/^[A-Za-z_]+(?:\/[A-Za-z0-9_+\-]+)*$/.test(value))
    return false;
  try {
    new Intl.DateTimeFormat('en', { timeZone: value });
    return true;
  } catch {
    return false;
  }
};
export const sessionSettingsError = (value: StudioRuntimeSettings | null | undefined) => {
  const session = value?.session;
  if (!session) return '';
  if (session.timezone !== undefined && !validConversationTimezone(session.timezone))
    return 'Enter a valid IANA conversation timezone or clear it to inherit native policy.';
  if (!integer(session.maxDurationSeconds, 10, 7200))
    return 'Maximum session duration must be an integer from 10 to 7,200 seconds.';
  if (!integer(session.silenceRetries, 0, 10))
    return 'Silence prompt attempts must be an integer from 0 to 10 within one conversation.';
  if (Object.values(session.silenceSeconds ?? {}).some((value) => !integer(value, 1, 3600)))
    return 'Each reply timeout must be an integer from 1 to 3,600 seconds.';
  return '';
};
const channels = [
  ['chat', 'Chat reply timeout (seconds)', 'Widget chat conversations.'],
  [
    'webcall',
    'Browser voice reply timeout (seconds)',
    'Browser voice: web, WebSocket and Widget voice conversations.',
  ],
  ['telephony', 'Telephony reply timeout (seconds)', 'Inbound and outbound phone conversations.'],
] as const;
export const StudioSessionSettings = ({
  value,
  onChange,
  disabled,
  squad = false,
}: {
  value: StudioRuntimeSettings | null | undefined;
  onChange: (value: StudioRuntimeSettings | null) => void;
  disabled: boolean;
  squad?: boolean;
}) => {
  const session = value?.session ?? {},
    locked = disabled || squad;
  const set = <K extends keyof Session>(key: K, next: Session[K] | undefined) => {
    if (locked) return;
    const result = { ...session };
    if (next === undefined) delete result[key];
    else result[key] = next;
    onChange(updateRuntimeGroup(value, 'session', result));
  };
  const number = (key: 'maxDurationSeconds' | 'silenceRetries', raw: string) => {
    if (!raw) set(key, undefined);
    else if (Number.isFinite(Number(raw))) set(key, Number(raw));
  };
  const problem = sessionSettingsError(value);
  return (
    <Stack gap={2}>
      <Typography variant="h6">Conversation settings</Typography>
      <Typography variant="body2" color="text.secondary">
        Explicit settings for conversations using this agent. Empty fields inherit native policy; no
        browser timezone, duration or silence defaults are copied. Save and publish the branch
        separately before testing.
      </Typography>
      {squad && (
        <Alert severity="warning">
          Session overrides require handoff-aware runtime support before use with a squad. Saved
          values remain visible.
        </Alert>
      )}
      <Button
        variant="outlined"
        disabled={disabled || value?.session === undefined}
        sx={{ alignSelf: 'start' }}
        onClick={() => onChange(updateRuntimeGroup(value, 'session', undefined))}>
        Clear session overrides
      </Button>
      <TextField
        label="Conversation timezone"
        value={session.timezone ?? ''}
        disabled={locked}
        error={session.timezone !== undefined && !validConversationTimezone(session.timezone)}
        helperText="IANA timezone for the agent's conversation clock, for example Europe/Kyiv. This does not change campaign dialing schedules. Empty inherits native policy."
        slotProps={{ htmlInput: { maxLength: 100 } }}
        onChange={(event) => set('timezone', event.target.value || undefined)}
      />
      <TextField
        label="Maximum session duration (seconds)"
        type="number"
        value={session.maxDurationSeconds ?? ''}
        disabled={locked}
        error={!integer(session.maxDurationSeconds, 10, 7200)}
        helperText="Integer 10–7,200 seconds per conversation. Empty inherits native policy."
        slotProps={{ htmlInput: { min: 10, max: 7200, step: 1 } }}
        onChange={(event) => number('maxDurationSeconds', event.target.value)}
      />
      <TextField
        label="Silence prompt attempts"
        type="number"
        value={session.silenceRetries ?? ''}
        disabled={locked}
        error={!integer(session.silenceRetries, 0, 10)}
        helperText="Integer 0–10 prompts within one conversation, not campaign redial attempts. Zero sends no silence prompts. Empty inherits native policy."
        slotProps={{ htmlInput: { min: 0, max: 10, step: 1 } }}
        onChange={(event) => number('silenceRetries', event.target.value)}
      />
      {channels.map(([key, label, help]) => (
        <TextField
          key={key}
          label={label}
          type="number"
          value={session.silenceSeconds?.[key] ?? ''}
          disabled={locked}
          error={!integer(session.silenceSeconds?.[key], 1, 3600)}
          helperText={`${help} Integer 1–3,600 seconds waiting for a reply. Empty inherits native policy.`}
          slotProps={{ htmlInput: { min: 1, max: 3600, step: 1 } }}
          onChange={(event) => {
            const raw = event.target.value;
            if (raw && !Number.isFinite(Number(raw))) return;
            const next = { ...session.silenceSeconds };
            if (!raw) delete next[key];
            else next[key] = Number(raw);
            set('silenceSeconds', Object.keys(next).length ? next : undefined);
          }}
        />
      ))}
      <TextField
        select
        label="Additional conversational style guidance"
        value={session.styleGuide === undefined ? 'native' : session.styleGuide ? 'on' : 'off'}
        disabled={locked}
        helperText="Callevate standard: concise natural replies, one question at a time, no spoken formatting. Job and business rules always take precedence. Native policy adds no explicit override."
        onChange={(event) => {
          set(
            'styleGuide',
            event.target.value === 'native' ? undefined : event.target.value === 'on',
          );
        }}>
        <MenuItem value="native">Use native policy</MenuItem>
        <MenuItem value="on">On (Callevate standard)</MenuItem>
        <MenuItem value="off">Off</MenuItem>
      </TextField>
      {problem && <Alert severity="warning">{problem}</Alert>}
    </Stack>
  );
};
